/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- Brand asset colors are intentionally static and not sourced from theme tokens. */
/**
 * Rovo animated logo (TREX-1527).
 *
 * Plays a 2.5s SMIL color-cycle animation on mount and freezes at the canonical
 * "rest" layout (saffron top-right, blue top-left). To re-trigger the animation,
 * give the component a new React `key` at the call site — browsers restart SMIL
 * when the SVG is re-inserted into the DOM:
 *
 *   <RovoAnimatedLogo key={proximityAnimationKey} colorMode={...} />
 *
 * The mouse-proximity handler in `AISmartButton` bumps the key counter each time
 * the cursor enters the proximity radius, which replays the animation cycle.
 *
 * Asset source: `rovo-animation-{light,dark}.svg`, generated from
 * `client/src/components/icons/RovoGlyph.tsx` (variant="full").
 *
 * Note on palette mapping: the LIGHT / DARK constants below are theme-keyed
 * (i.e. LIGHT is used in light mode, DARK is used in dark mode), and the
 * hex values are intentionally kept in sync with the existing static
 * `RovoColorLogo` component. Keeping the two components on the same
 * palette is important — when the proximity animation starts and React
 * swaps `RovoColorLogo` out for `RovoAnimatedLogo`, any mismatch would
 * appear as a one-frame color flash to the user.
 */
import React, { useEffect, useId, useState } from 'react';

import { bind } from 'bind-event-listener';

import { RovoColorLogo } from './RovoColorLogo';

type RovoAnimatedLogoProps = {
	colorMode: 'light' | 'dark';
};

/**
 * Returns true if the user has requested reduced motion via their OS / browser
 * settings (CSS `prefers-reduced-motion: reduce`). Subscribes to live changes
 * so a user flipping the setting mid-session sees the update.
 *
 * The proximity-trigger code in `AISmartButton` already guards against
 * `prefers-reduced-motion` before bumping the React key. This hook is a
 * defense-in-depth check so that any other call site that mounts
 * `RovoAnimatedLogo` directly (tests, Storybook, future reuse) also respects
 * the user's preference.
 */
const useReducedMotion = (): boolean => {
	const [reduced, setReduced] = useState<boolean>(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return false;
		}
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	});

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return;
		}
		const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
		const unbind = bind(mq, {
			type: 'change',
			listener: (e: MediaQueryListEvent) => setReduced(e.matches),
		});
		return unbind;
	}, []);

	return reduced;
};

const SVG_SIZE = 24;

// Light-mode palette — kept in sync with RovoColorLogo's `light` entry.
const LIGHT = {
	blue: '#357DE8',
	green: '#82B536',
	purple: '#BF63F3',
	yellow: '#FCA700',
};

// Dark-mode palette — kept in sync with RovoColorLogo's `dark` entry.
const DARK = {
	blue: '#1868DB',
	green: '#6A9A23',
	purple: '#AF59E0',
	yellow: '#FCA700',
};

export const RovoAnimatedLogo = ({ colorMode }: RovoAnimatedLogoProps): React.JSX.Element => {
	// Hook calls — must all run in the same order every render (Rules of
	// Hooks). Any conditional bailout below MUST come AFTER all hooks.
	const prefersReducedMotion = useReducedMotion();
	// useId() guarantees per-instance unique SVG <defs> IDs so multiple
	// concurrent renders of this component don't cross-reference each other's
	// masks / gradients (which would silently mis-render).
	const uid = useId();

	// WCAG 2.3.3: never render the SMIL color-cycle animation for users who
	// have requested reduced motion. Fall back to the existing static
	// RovoColorLogo, which matches the look of the animation's frozen "rest"
	// frame. This is intentionally a defense-in-depth check; the
	// proximity-trigger code in AISmartButton already guards too.
	if (prefersReducedMotion) {
		return <RovoColorLogo colorMode={colorMode} />;
	}

	// Theme-keyed palette selection. Stay in sync with RovoColorLogo so the
	// two components show identical colors at rest (no flash on swap).
	const c = colorMode === 'dark' ? DARK : LIGHT;
	const ID_CLIP = `rg-clip-${uid}`;
	const ID_MASK_LEFT = `rg-mask-left-${uid}`;
	const ID_MASK_RIGHT = `rg-mask-right-${uid}`;
	const ID_GRAD_LEFT = `rg-grad-left-${uid}`;
	const ID_GRAD_RIGHT = `rg-grad-right-${uid}`;
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={SVG_SIZE}
			height={SVG_SIZE}
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
			focusable="false"
		>
			<defs>
				<clipPath id={ID_CLIP}>
					<rect width="16" height="16" />
				</clipPath>
				<mask id={ID_MASK_LEFT} maskUnits="userSpaceOnUse">
					<rect width="16" height="16" fill="black" />
					<path
						fill="white"
						fillRule="evenodd"
						clipRule="evenodd"
						d="M7.53088 10.4476L2.73535 13.2163L1.78016 12.6651C1.21921 12.3417 0.87207 11.7418 0.87207 11.0954V4.91065C0.87207 4.26171 1.21717 3.66436 1.77979 3.34113L5.80879 1.0166C5.79985 1.04169 5.79134 1.06695 5.78324 1.09236C5.70866 1.3238 5.66912 1.56833 5.66912 1.81827V8.00303C5.66912 8.85259 6.12167 9.63627 6.85915 10.0601L7.53088 10.4476Z"
					/>
					<path
						fill="white"
						fillRule="evenodd"
						clipRule="evenodd"
						d="M7.53088 10.4475L2.73535 13.2162L7.13997 15.7575C7.69088 16.075 8.36884 16.0807 8.92424 15.7743C8.9343 15.7665 8.945 15.7593 8.95634 15.7528C9.35211 15.5246 9.64153 15.1587 9.77761 14.7361C9.83299 14.5613 9.86226 14.3767 9.86226 14.1877V11.792L7.53088 10.4475Z"
					/>
				</mask>
				<mask id={ID_MASK_RIGHT} maskUnits="userSpaceOnUse">
					<rect width="16" height="16" fill="black" />
					<path
						fill="white"
						fillRule="evenodd"
						clipRule="evenodd"
						d="M14.318 3.33493L13.2907 2.74219L8.49512 5.5109L9.23931 5.94007C9.97691 6.36392 10.4291 7.14744 10.4291 7.99708V14.1818C10.4291 14.4321 10.3895 14.677 10.3147 14.9087C10.3067 14.9338 10.2983 14.9587 10.2895 14.9835L14.3185 12.659C14.8811 12.3357 15.2262 11.7384 15.2262 11.0895V4.90471C15.2262 4.25833 14.879 3.65833 14.318 3.33493Z"
					/>
					<path
						fill="white"
						fillRule="evenodd"
						clipRule="evenodd"
						d="M8.49356 5.51082L6.23438 4.20796V1.81225C6.23438 1.62347 6.26358 1.43906 6.31885 1.26449C6.45471 0.841993 6.74369 0.476235 7.13903 0.247911L7.14023 0.247219C7.15156 0.240681 7.16234 0.233429 7.1724 0.225631C7.72783 -0.0807372 8.40575 -0.0751089 8.95668 0.242516L13.2891 2.74211L8.49356 5.51082Z"
					/>
				</mask>
				<linearGradient
					id={ID_GRAD_LEFT}
					x1="0"
					y1="-33.19"
					x2="27.7"
					y2="14.81"
					gradientUnits="userSpaceOnUse"
					spreadMethod="repeat"
				>
					<stop offset="0%" stopColor={c.purple} />
					<stop offset="24.999%" stopColor={c.purple} />
					<stop offset="25%" stopColor={c.yellow} />
					<stop offset="49.999%" stopColor={c.yellow} />
					<stop offset="50%" stopColor={c.blue} />
					<stop offset="74.999%" stopColor={c.blue} />
					<stop offset="75%" stopColor={c.green} />
					<stop offset="99.999%" stopColor={c.green} />
				</linearGradient>
				<linearGradient
					id={ID_GRAD_RIGHT}
					x1="0"
					y1="-37.57"
					x2="27.7"
					y2="10.43"
					gradientUnits="userSpaceOnUse"
					spreadMethod="repeat"
				>
					<stop offset="0%" stopColor={c.green} />
					<stop offset="24.999%" stopColor={c.green} />
					<stop offset="25%" stopColor={c.blue} />
					<stop offset="49.999%" stopColor={c.blue} />
					<stop offset="50%" stopColor={c.yellow} />
					<stop offset="74.999%" stopColor={c.yellow} />
					<stop offset="75%" stopColor={c.purple} />
					<stop offset="99.999%" stopColor={c.purple} />
				</linearGradient>
			</defs>

			<g clipPath={`url(#${ID_CLIP})`}>
				{/* Gradient layer — the two halves animate translateY independently,
				    in opposite directions. Each rect is masked to one half of the
				    button, and the gradient stops cycle through the 4 brand colors.
				    The `animateTransform` translates by exactly one full gradient
				    period, then `fill="freeze"` locks the end state. There is no
				    separate "rest" layer underneath: the gradient at phase 0 IS the
				    rest state, so removing the rest layer eliminates the
				    color-mismatch flash that occurred when the two layers'
				    palettes diverged. */}
				<g>
					<g mask={`url(#${ID_MASK_LEFT})`}>
						<g>
							<rect
								x="0"
								y="-63.985208333"
								width="16"
								height="191.955625"
								fill={`url(#${ID_GRAD_LEFT})`}
							/>
							<animateTransform
								attributeName="transform"
								type="translate"
								values="0 0; 0 -63.985208333"
								keyTimes="0; 1"
								keySplines="0.4 0 0 1"
								calcMode="spline"
								dur="2.5s"
								begin="0s"
								repeatCount="1"
								fill="freeze"
							/>
						</g>
					</g>
					<g mask={`url(#${ID_MASK_RIGHT})`}>
						<g>
							<rect
								x="0"
								y="-63.985208333"
								width="16"
								height="191.955625"
								fill={`url(#${ID_GRAD_RIGHT})`}
							/>
							<animateTransform
								attributeName="transform"
								type="translate"
								values="0 0; 0 63.985208333"
								keyTimes="0; 1"
								keySplines="0.4 0 0 1"
								calcMode="spline"
								dur="2.5s"
								begin="0s"
								repeatCount="1"
								fill="freeze"
							/>
						</g>
					</g>
				</g>
			</g>
		</svg>
	);
};
